import { useEffect, type FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import type { EventContainer } from '@trello/analytics-types';
import { useFeatureGate } from '@trello/feature-gate-client';
import { intl } from '@trello/i18n';
import { ActivityIcon } from '@trello/nachos/icons/activity';
import { OrganizationVisibleIcon } from '@trello/nachos/icons/organization-visible';

import { WorkspacePageHeader } from 'app/src/components/WorkspacePages/WorkspacePageHeader';
import { MemberActivityList } from './MemberActivityList';
import { useMemberActivityViewQuery } from './MemberActivityViewQuery.generated';
import { MemberActivityWorkspaces } from './MemberActivityWorkspaces';

import styles from './MemberActivityView.less';

interface MemberActivityViewProps {
  isMemberMe: boolean;
  memberId: string;
  taskSucceeded: ({ containers }: { containers?: EventContainer }) => void;
}

export const MemberActivityView: FunctionComponent<MemberActivityViewProps> = ({
  isMemberMe,
  memberId,
  taskSucceeded,
}) => {
  const { data } = useMemberActivityViewQuery({
    variables: {
      memberId,
    },
    waitOn: ['MemberBoards'],
  });

  const { value: isBillplatWorkspaceSettingsEnabled } = useFeatureGate(
    'billplat_workspace_settings_navigation',
  );

  const orgs = data?.member?.organizations;
  // check if any of the orgs have a permission level of public
  const hasPublicOrg = orgs?.some(
    (org) => org.prefs?.permissionLevel === 'public',
  );

  useEffect(() => {
    // in parity with the previous view app/scripts/views/member/MemberActivityView.tsx
    // no extra context is collected, thereforce just leaving it as a task success tied to the original traceid
    taskSucceeded({});
  }, [taskSucceeded]);

  return (
    <div className={styles.activityContainer}>
      {isBillplatWorkspaceSettingsEnabled && (
        <WorkspacePageHeader
          title={intl.formatMessage({
            id: 'page title.activity',
            defaultMessage: 'Activity',
            description: 'Activity',
          })}
        />
      )}

      {(hasPublicOrg || isMemberMe) && (
        <div className={styles.windowModule}>
          <div className={styles.windowModuleTitle}>
            <span className={styles.windowModuleTitleIcon}>
              <OrganizationVisibleIcon size="large" label="Workspaces" />
            </span>
            <h3>
              <FormattedMessage
                id="view title.teams"
                defaultMessage="Workspaces"
                description="Workspaces"
              />
            </h3>
          </div>
          <MemberActivityWorkspaces
            memberId={memberId}
            isMemberMe={isMemberMe}
          />
        </div>
      )}

      <div className={styles.windowModule}>
        <div className={styles.windowModuleTitle}>
          <span className={styles.windowModuleTitleIcon}>
            <ActivityIcon size="large" label="Activity" />
          </span>
          <h3>
            <FormattedMessage
              id="templates.member_activity.activity"
              defaultMessage="Activity"
              description="Activity"
            />
          </h3>
          <MemberActivityList memberId={memberId} />
        </div>
      </div>
    </div>
  );
};
